/**
 * CityTemperatureLayer Component
 *
 * Displays current temperatures and air quality for major Australian cities on the map,
 * similar to how Windy.com shows temperature labels.
 *
 * Features:
 * - Temperature display with city name
 * - Weather icon based on weather code
 * - AQI (Air Quality Index) value with color coding
 * - Auto-refresh every 15 minutes (temp) / 5 minutes (AQI)
 * - Only shows when map is zoomed out enough
 */

import { useEffect, useState, useMemo } from 'react';
import { Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { fetchCityTemperatures, CityTemperature } from '../api/client';

// Air quality JSON URL - served from CloudFront
const AIR_QUALITY_URL = import.meta.env.VITE_AIR_QUALITY_URL || '';

interface AirQualityPoint {
  lat: number;
  lon: number;
  aqi: number | null;
}

interface AirQualityData {
  data: AirQualityPoint[];
}

// Refresh intervals
const TEMP_REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes for temp
const AQI_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes for AQI

// Zoom thresholds for showing cities (expanded for ~270 towns)
const MAX_ZOOM_FOR_LABELS = 12; // Hide all when zoomed in past this
const REGIONAL_ZOOM_THRESHOLD = 6; // Show regional towns only when zoomed in past this
const SMALL_TOWN_ZOOM_THRESHOLD = 7; // Show smaller towns at higher zoom
const TINY_TOWN_ZOOM_THRESHOLD = 8; // Show tiny towns when zoomed in more

// City classification by index (backend returns ~270 towns sorted by importance)
const MAJOR_CITY_COUNT = 25;      // Major cities always shown
const REGIONAL_TOWN_COUNT = 70;   // Regional towns at REGIONAL_ZOOM_THRESHOLD
const SMALL_TOWN_COUNT = 150;     // Small towns at SMALL_TOWN_ZOOM_THRESHOLD

/**
 * Get AQI color for display (matching US EPA scale)
 */
function getAQIColor(aqi: number): string {
  if (aqi <= 50) return '#00e400'; // Good - green
  if (aqi <= 100) return '#ffff00'; // Moderate - yellow
  if (aqi <= 150) return '#ff7e00'; // USG - orange
  if (aqi <= 200) return '#ff0000'; // Unhealthy - red
  if (aqi <= 300) return '#8f3f97'; // Very Unhealthy - purple
  return '#7e0023'; // Hazardous - maroon
}

/**
 * Get AQI health description for tooltip (US EPA scale)
 */
function getAQIDescription(aqi: number): string {
  if (aqi <= 50) return 'Good - Air quality is satisfactory, little or no health risk';
  if (aqi <= 100) return 'Moderate - Acceptable; some pollutants may affect very sensitive individuals';
  if (aqi <= 150) return 'Unhealthy for Sensitive Groups - May affect children, elderly, and those with respiratory conditions';
  if (aqi <= 200) return 'Unhealthy - Everyone may experience health effects; sensitive groups more serious';
  if (aqi <= 300) return 'Very Unhealthy - Health alert; everyone may experience serious health effects';
  return 'Hazardous - Health warning; entire population likely affected';
}

/**
 * Find the nearest AQI value for a given lat/lon from the grid data
 */
function findNearestAQI(lat: number, lon: number, aqiData: AirQualityPoint[]): number | null {
  if (!aqiData || aqiData.length === 0) return null;

  let nearestAqi: number | null = null;
  let minDistance = Infinity;

  for (const point of aqiData) {
    if (point.aqi === null) continue;
    // Simple Euclidean distance (good enough for finding nearest grid point)
    const distance = Math.sqrt(
      Math.pow(point.lat - lat, 2) + Math.pow(point.lon - lon, 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearestAqi = point.aqi;
    }
  }

  return nearestAqi;
}

interface CityTemperatureLayerProps {
  enabled?: boolean;
}

/**
 * Get weather description and icon based on WMO weather code.
 * https://open-meteo.com/en/docs#weathervariables
 */
function getWeatherInfo(code: number | null): { icon: string; description: string } {
  if (code === null) return { icon: '?', description: 'Unknown' };

  // Clear
  if (code === 0) return { icon: '☀️', description: 'Clear' };

  // Mainly clear, partly cloudy
  if (code === 1) return { icon: '🌤️', description: 'Mainly clear' };
  if (code === 2) return { icon: '⛅', description: 'Partly cloudy' };
  if (code === 3) return { icon: '☁️', description: 'Overcast' };

  // Fog
  if (code === 45 || code === 48) return { icon: '🌫️', description: 'Fog' };

  // Drizzle
  if (code >= 51 && code <= 55) return { icon: '🌧️', description: 'Drizzle' };
  if (code >= 56 && code <= 57) return { icon: '🌧️', description: 'Freezing drizzle' };

  // Rain
  if (code >= 61 && code <= 65) return { icon: '🌧️', description: 'Rain' };
  if (code >= 66 && code <= 67) return { icon: '🌧️', description: 'Freezing rain' };

  // Snow
  if (code >= 71 && code <= 77) return { icon: '❄️', description: 'Snow' };

  // Showers
  if (code >= 80 && code <= 82) return { icon: '🌦️', description: 'Rain showers' };
  if (code >= 85 && code <= 86) return { icon: '🌨️', description: 'Snow showers' };

  // Thunderstorm
  if (code >= 95 && code <= 99) return { icon: '⛈️', description: 'Thunderstorm' };

  return { icon: '?', description: 'Unknown' };
}

/**
 * Get short AQI label for the badge
 */
function getAQILabel(aqi: number): string {
  if (aqi <= 50) return 'Good';
  if (aqi <= 100) return 'Moderate';
  if (aqi <= 150) return 'Sensitive';
  if (aqi <= 200) return 'Unhealthy';
  if (aqi <= 300) return 'Very Unhealthy';
  return 'Hazardous';
}

/**
 * Create a custom icon for city temperature display with AQI.
 * Shows weather icon, temperature, and color-coded AQI value with custom tooltip.
 */
function createTemperatureIcon(city: CityTemperature, aqi: number | null): L.DivIcon {
  const temp = city.temperature !== null ? Math.round(city.temperature) : '?';
  const weather = getWeatherInfo(city.weatherCode);

  // Use white text with strong dark outline for maximum readability
  const tempColor = '#ffffff';

  // AQI display - show colored badge with custom CSS tooltip
  const aqiHtml = aqi !== null ? `
    <span class="aqi-badge-wrapper" style="position: relative; pointer-events: auto;">
      <span
        class="aqi-badge"
        style="
          font-size: 11px;
          font-weight: 700;
          color: #000;
          background: ${getAQIColor(aqi)};
          padding: 1px 4px;
          border-radius: 3px;
          margin-left: 2px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.5);
          cursor: help;
          display: inline-block;
        "
      >AQI ${Math.round(aqi)}</span>
      <span class="aqi-tooltip" style="
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        background: rgba(20, 20, 20, 0.95);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        color: #fff;
        padding: 10px 14px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.2s, visibility 0.2s;
        box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1);
        z-index: 10000;
        pointer-events: none;
        text-align: center;
        line-height: 1.4;
      ">
        <span style="
          display: block;
          font-size: 13px;
          font-weight: 700;
          color: ${getAQIColor(aqi)};
          margin-bottom: 4px;
        ">${getAQILabel(aqi)}</span>
        <span style="
          display: block;
          font-size: 11px;
          color: #bbb;
          max-width: 200px;
          white-space: normal;
        ">${getAQIDescription(aqi)}</span>
        <span style="
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid rgba(20, 20, 20, 0.95);
        "></span>
      </span>
    </span>
  ` : '';

  return L.divIcon({
    className: 'city-temp-marker',
    html: `
      <div style="
        display: flex;
        align-items: center;
        gap: 3px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        pointer-events: none;
        padding-top: 21px;
      ">
        <span style="font-size: 14px; filter: drop-shadow(0 1px 3px rgba(0,0,0,0.9));">${weather.icon}</span>
        <span style="
          font-size: 15px;
          font-weight: 700;
          color: ${tempColor};
          text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px 1px 0 #000,
            1px 1px 0 #000,
            0 0 4px rgba(0,0,0,0.9);
        ">${temp}°</span>
        ${aqiHtml}
      </div>
    `,
    iconSize: [100, 40],
    iconAnchor: [50, 12],
  });
}

export default function CityTemperatureLayer({ enabled = true }: CityTemperatureLayerProps) {
  const map = useMap();
  const [cities, setCities] = useState<CityTemperature[]>([]);
  const [aqiData, setAqiData] = useState<AirQualityPoint[]>([]);
  const [zoom, setZoom] = useState(map.getZoom());
  // Viewport bounds so we only build icons for towns in view (not all ~270
  // nationwide). No town is dropped — off-screen ones appear when panned to.
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds>(() => map.getBounds());

  // Track zoom level + viewport bounds
  useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
      setViewBounds(map.getBounds());
    },
    moveend: () => {
      setViewBounds(map.getBounds());
    },
  });

  // Fetch city temperatures
  useEffect(() => {
    if (!enabled) return;

    const fetchData = async () => {
      try {
        const response = await fetchCityTemperatures();
        setCities(response.cities);
      } catch (error) {
        console.error('Failed to fetch city temperatures:', error);
      }
    };

    fetchData();

    // Refresh periodically
    const interval = setInterval(fetchData, TEMP_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled]);

  // Fetch air quality data separately (more frequent refresh)
  useEffect(() => {
    if (!enabled || !AIR_QUALITY_URL) return;

    const fetchAQI = async () => {
      try {
        const response = await fetch(AIR_QUALITY_URL);
        if (response.ok) {
          const data: AirQualityData = await response.json();
          setAqiData(data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch air quality:', error);
      }
    };

    fetchAQI();

    // Refresh AQI more frequently
    const interval = setInterval(fetchAQI, AQI_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled]);

  // Create icons for visible cities with AQI
  // Show cities at different zoom levels based on importance (~270 towns total)
  const cityMarkers = useMemo(() => {
    if (!enabled || zoom > MAX_ZOOM_FOR_LABELS || cities.length === 0) {
      return [];
    }

    // Only build icons for towns within the current viewport (+ padding), so a
    // regional view doesn't render (and rebuild on every zoom) all ~270 towns.
    const paddedBounds = viewBounds.pad(0.25);

    // Filter cities based on zoom level with graduated visibility
    const visibleCities = cities.filter((city, index) => {
      if (city.temperature === null) return false;
      if (!paddedBounds.contains([city.lat, city.lon])) return false; // off-screen
      // Always show major cities (capitals + major regional)
      if (index < MAJOR_CITY_COUNT) return true;
      // Regional towns visible at zoom 6+
      if (index < REGIONAL_TOWN_COUNT) return zoom >= REGIONAL_ZOOM_THRESHOLD;
      // Small towns visible at zoom 7+
      if (index < SMALL_TOWN_COUNT) return zoom >= SMALL_TOWN_ZOOM_THRESHOLD;
      // Tiny towns visible at zoom 8+
      return zoom >= TINY_TOWN_ZOOM_THRESHOLD;
    });

    return visibleCities.map(city => {
      const aqi = findNearestAQI(city.lat, city.lon, aqiData);
      return {
        city,
        icon: createTemperatureIcon(city, aqi),
      };
    });
  }, [cities, aqiData, enabled, zoom, viewBounds]);

  if (!enabled || zoom > MAX_ZOOM_FOR_LABELS) {
    return null;
  }

  return (
    <>
      {cityMarkers.map(({ city, icon }) => (
        <Marker
          key={city.name}
          position={[city.lat, city.lon]}
          icon={icon}
          interactive={false}
        />
      ))}
    </>
  );
}
